import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable, Subject, map, take } from 'rxjs';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { ErscheinungsbildComponent } from '../erscheinungsbild/erscheinungsbild.component';
import { warnBeforeUnload } from '../../shared/forms/warn-before-unload';
import { BereichId, STANDARD_BEREICH, VERWALTUNG_BEREICHE, bereichAusParam } from './verwaltung-bereiche';
import { VerwaltungBenutzerComponent } from '../verwaltung-benutzer/verwaltung-benutzer.component';
import { VerwaltungProjekteComponent } from '../verwaltung-projekte/verwaltung-projekte.component';
import { VerwaltungNavComponent } from './verwaltung-nav.component';
import { VerwaltungKiComponent } from './verwaltung-ki.component';
import { VerwaltungStore } from './verwaltung.store';
import { VwAuswahllistenComponent } from './vw-auswahllisten.component';
import { VwKopfComponent } from './vw-kopf.component';
import { VwRechnerComponent } from '../vw-rechner/vw-rechner.component';

@Component({
  selector: 'app-verwaltung-page',
  standalone: true,
  imports: [
    ConfirmDialogComponent,
    ErscheinungsbildComponent,
    TranslatePipe,
    VerwaltungBenutzerComponent,
    VerwaltungNavComponent,
    VerwaltungKiComponent,
    VerwaltungProjekteComponent,
    VwKopfComponent,
    VwAuswahllistenComponent,
    VwRechnerComponent,
  ],
  templateUrl: './verwaltung-page.component.html',
  styleUrl: './verwaltung-page.component.scss',
  providers: [VerwaltungStore],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VerwaltungPageComponent {
  readonly store = inject(VerwaltungStore);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly bereiche = inject(VERWALTUNG_BEREICHE).flatMap((g) => g.bereiche);

  readonly bereichId = toSignal(
    this.route.queryParamMap.pipe(map((p) => bereichAusParam(p.get('bereich')) ?? STANDARD_BEREICH)),
    { requireSync: true },
  );
  readonly bereich = computed(() => this.bereiche.find((b) => b.id === this.bereichId())!);
  readonly rueckfrageOffen = signal(false);

  private antwort: Subject<boolean> | null = null;

  constructor() {
    this.route.queryParamMap.pipe(takeUntilDestroyed()).subscribe((p) => {
      const reiter = p.get('reiter');
      const roh = p.get('bereich');
      if (reiter !== null) {
        this.adresseErsetzen(reiter === 'ki' ? { bereich: 'ki' } : {});
      } else if (roh !== null && bereichAusParam(roh) === null) {
        this.adresseErsetzen({});
      }
    });
    warnBeforeUnload(() => this.store.aktiverBereich()?.hatUngespeicherteAenderungen() ?? false);
  }

  bereichWaehlen(id: BereichId): void {
    if (id === this.bereichId()) return;
    void this.router.navigate([], { relativeTo: this.route, queryParams: { bereich: id } });
  }

  confirmDeactivate(): Observable<boolean> {
    this.antwortGeben(false);
    this.antwort = new Subject<boolean>();
    this.rueckfrageOffen.set(true);
    return this.antwort.pipe(take(1));
  }

  rueckfrageBeantworten(verwerfen: boolean): void {
    this.rueckfrageOffen.set(false);
    if (verwerfen) {
      this.store.aktiverBereich()?.verwerfen();
    }
    this.antwortGeben(verwerfen);
  }

  private antwortGeben(wert: boolean): void {
    this.antwort?.next(wert);
    this.antwort?.complete();
    this.antwort = null;
  }

  private adresseErsetzen(queryParams: Params): void {
    void this.router.navigate([], { relativeTo: this.route, queryParams, replaceUrl: true });
  }
}
